/**
 * Component: UniqueUsersChart
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R10
 *
 * Chart component displaying unique user interactions per day and month.
 * ADMIN-V1-R10: System shall provide a usage dashboard displaying unique user
 *               interactions per day and month.
 * Accessibility: WCAG 2.1 AA compliant with proper ARIA labels
 */

import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { format, parseISO } from 'date-fns';

import { Button } from '@/components/ui/button';

/**
 * Data point for unique users over time
 */
export interface UniqueUsersDataPoint {
  /** ISO 8601 timestamp */
  timestamp: string;
  /** Number of unique users for this time period */
  uniqueUsers: number;
  /** Total interactions for this time period */
  totalInteractions?: number;
}

export interface UniqueUsersChartProps {
  /** Array of daily data points ordered by timestamp */
  dailyData: UniqueUsersDataPoint[];
  /** Array of monthly data points ordered by timestamp */
  monthlyData: UniqueUsersDataPoint[];
  /** Chart height in pixels (default: 300) */
  height?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Accessible description for screen readers */
  description?: string;
}

type ViewMode = 'daily' | 'monthly';

/**
 * Custom tooltip component for the chart
 */
interface TooltipPayload {
  timestamp: string;
  uniqueUsers: number;
  totalInteractions?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: TooltipPayload }>;
  viewMode: ViewMode;
}

function CustomTooltip({ active, payload, viewMode }: CustomTooltipProps): React.ReactElement | null {
  if (!active || !payload?.[0]) {
    return null;
  }

  const data = payload[0].payload;
  const date = parseISO(data.timestamp);
  const dateFormat = viewMode === 'daily' ? 'MMMM d, yyyy' : 'MMMM yyyy';

  return (
    <div
      className="rounded-md border bg-background p-3 shadow-md"
      role="tooltip"
    >
      <p className="text-sm font-medium">
        {format(date, dateFormat)}
      </p>
      <p className="text-sm text-muted-foreground">
        Unique users: <span className="font-medium text-foreground">{data.uniqueUsers.toLocaleString()}</span>
      </p>
      {data.totalInteractions !== undefined && (
        <p className="text-sm text-muted-foreground">
          Total interactions: <span className="font-medium text-foreground">{data.totalInteractions.toLocaleString()}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Loading skeleton for the chart
 */
function ChartSkeleton({ height }: { height: number }): React.ReactElement {
  return (
    <div
      className="animate-pulse rounded-md bg-muted"
      style={{ height }}
      aria-label="Loading chart"
      role="progressbar"
    />
  );
}

/**
 * Empty state when no data is available
 */
function EmptyState(): React.ReactElement {
  return (
    <div
      className="flex h-full min-h-[200px] items-center justify-center rounded-md border border-dashed"
      role="status"
    >
      <p className="text-sm text-muted-foreground">
        No user interaction data available for the selected period
      </p>
    </div>
  );
}

/**
 * Area chart displaying unique user interactions per day and month.
 *
 * ADMIN-V1-R10: Provides visual representation of unique user
 * interactions over time with daily and monthly view modes.
 */
export function UniqueUsersChart({
  dailyData,
  monthlyData,
  height = 300,
  isLoading = false,
  description = 'Chart showing unique user interactions over time',
}: UniqueUsersChartProps): React.ReactElement {
  const [viewMode, setViewMode] = useState<ViewMode>('daily');

  // Select data based on view mode
  const data = viewMode === 'daily' ? dailyData : monthlyData;
  const dateFormat = viewMode === 'daily' ? 'MMM d' : 'MMM yyyy';

  // Format data for chart consumption
  const chartData = useMemo(() => {
    return data.map((point) => ({
      ...point,
      date: format(parseISO(point.timestamp), dateFormat),
    }));
  }, [data, dateFormat]);

  // Calculate Y-axis domain for consistent scaling
  const yDomain = useMemo(() => {
    if (data.length === 0) return [0, 100];
    const maxUsers = Math.max(...data.map((d) => d.uniqueUsers));
    // Add 10% padding to max value, minimum of 10
    return [0, Math.max(10, Math.ceil(maxUsers * 1.1))];
  }, [data]);

  if (isLoading) {
    return <ChartSkeleton height={height} />;
  }

  if (data.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex gap-2" role="group" aria-label="Select view mode">
        <Button
          variant={viewMode === 'daily' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('daily')}
          aria-pressed={viewMode === 'daily'}
        >
          Daily
        </Button>
        <Button
          variant={viewMode === 'monthly' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('monthly')}
          aria-pressed={viewMode === 'monthly'}
        >
          Monthly
        </Button>
      </div>

      {/* Chart */}
      <div
        role="img"
        aria-label={`${description}. Currently showing ${viewMode} view.`}
        className="w-full"
      >
        {/* Screen reader description */}
        <span className="sr-only">
          {description}.
          Currently showing {viewMode} data.
          {data.length > 0 && data[0] && (
            <> Data ranges from {format(parseISO(data[0].timestamp), viewMode === 'daily' ? 'MMMM d, yyyy' : 'MMMM yyyy')}</>
          )}
          {data.length > 0 && (() => {
            const lastItem = data[data.length - 1];
            return lastItem ? <> to {format(parseISO(lastItem.timestamp), viewMode === 'daily' ? 'MMMM d, yyyy' : 'MMMM yyyy')}</> : null;
          })()}
          . Total data points: {data.length}.
        </span>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="uniqueUsersGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-muted"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              className="text-muted-foreground"
              dy={10}
            />
            <YAxis
              domain={yDomain}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              className="text-muted-foreground"
              width={50}
              tickFormatter={(value: number) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip viewMode={viewMode} />} />
            <Area
              type="monotone"
              dataKey="uniqueUsers"
              stroke="hsl(var(--primary))"
              fill="url(#uniqueUsersGradient)"
              strokeWidth={2}
              name="Unique Users"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default UniqueUsersChart;
